import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type LivePoliteness = 'polite' | 'assertive';

interface AnnounceContextValue {
  announce: (text: string, politeness?: LivePoliteness) => void;
}

const AnnounceContext = createContext<AnnounceContextValue | null>(null);

interface AnnounceProviderProps {
  children: ReactNode;
}

// Mount once at the application root. Renders two visually hidden live
// regions (polite + assertive) and exposes an `announce()` function through
// context. Components anywhere in the tree can call `useAnnounce()` to push
// a message to screen readers without having to wire up their own region.
export function AnnounceProvider({ children }: AnnounceProviderProps) {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');

  const announce = useCallback(
    (text: string, politeness: LivePoliteness = 'polite') => {
      const setter =
        politeness === 'assertive' ? setAssertiveMessage : setPoliteMessage;
      // Clear, then set on the next frame so identical consecutive messages
      // still trigger an announcement.
      setter('');
      requestAnimationFrame(() => setter(text));
    },
    []
  );

  const value = useMemo(() => ({ announce }), [announce]);

  return (
    <AnnounceContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-cy="announce-polite"
      >
        {politeMessage}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
        data-cy="announce-assertive"
      >
        {assertiveMessage}
      </div>
    </AnnounceContext.Provider>
  );
}

export default function useAnnounce(): AnnounceContextValue {
  const ctx = useContext(AnnounceContext);
  if (!ctx) {
    // Fail open: if a component is rendered outside the provider (e.g. in a
    // standalone unit test) the announce call becomes a noop instead of
    // crashing the tree.
    return { announce: () => {} };
  }
  return ctx;
}
